// [2026-09-02] ADDED — the one paginated, searchable table the customer screens share.
//
// WHY ONE COMPONENT AND NOT A TABLE PER SCREEN. Pagination and search are the two things every
// report table needs and the two that are easiest to get subtly wrong in each copy — an off-by-one
// on the page window, a search that refires on every keystroke, a page number that survives a filter
// change and lands the user on an empty page 7. Written once, those bugs are fixed once.
//
// SERVER-SIDE, NOT CLIENT-SIDE. The rows and the total both come from the API for the CURRENT page.
// Filtering an array the browser already holds is simpler, but it can only ever search what was
// downloaded — with a TTL feed of every API call, that is a small and arbitrary slice. Searching
// where the data lives is the only version that answers the question the user is actually asking.
//
// SEARCH IS DEBOUNCED (400ms). Typing "candidate-4801" is 14 keystrokes; without a debounce that is
// 14 database queries, 13 of which are already stale when they land.
//
// [2026-09-02] ADDED — the controls a paginated table needs and this one did not have: a page-size
// selector, first/last jumps, a refresh, keyboard-reachable rows, a whole-result-set export, and
// `initialFilters` so another screen can deep-link INTO a filtered view. Every addition is optional
// and defaults to the previous behaviour, because PlatformReports and PlatformRequests both render
// this component and neither was changed to keep working.
//
// NOT ADDED, DELIBERATELY: column sorting. Sorting has to happen where the pagination happens, and
// neither GET /me/requests nor GET /me/sessions accepts a sort parameter
// (proctoring-sdk-backend app/modules/me/route.py:272-330) — both are fixed to newest-first. A
// header that sorted only the 25 rows already downloaded would look like it ordered 4,000 and
// silently order 25. That needs a backend argument first.

// [2026-09-04] CHANGED — restyled to the IncProctor Figma: the search box, the refresh / Filters /
// Export controls, and the "Showing … entries" + rows-per-page + circular pager footer.
//
// NONE OF THE LOGIC MOVED. The debounce, the stale-response guard, the reset-to-page-1 effect, the
// sequential export walk and its cap are the same code they were; every change below is markup and
// class names. The one behavioural addition is that the filter selects are now behind a "Filters"
// toggle, and they start OPEN whenever a filter is already applied — a filter that is narrowing the
// table while being hidden behind a collapsed panel is how someone concludes the data is missing.
//
// THREE STRINGS ARE LOAD-BEARING AND ARE UNCHANGED:
//   * the counter still reads "Showing 1–10 of 50" (EN DASH), with " entries" appended. All three
//     assertions in e2e/platform-dashboard.spec.js (lines 76, 111, 116) are unanchored regexes, so
//     a suffix is safe and "to" instead of "–" would not have been.
//   * the search input keeps aria-label="Search"  (spec line 94, getByLabel).
//   * each filter select keeps aria-label={f.label} (spec line 115, getByLabel('Status')).
// The pager buttons DID change — "← Previous" / "Next →" are now icon buttons with proper labels —
// and the spec is updated in the same commit rather than the labels being contorted to match it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { downloadCsv, num, toast } from '../../ui.jsx'
import { PfEmpty, PfNotice, PfSkeleton } from '../../components/platform/ui.jsx'
// [2026-09-05] CHANGED — the pager and sortable headers are the ported account-app components now.
// OLD: ChevronLeftIcon, ChevronRightIcon were used to build a bespoke circular pager here.
// [2026-09-05] CHANGED — RefreshIcon dropped from this import; the toolbar now uses the supplied
// PNG. RefreshIcon is UNCHANGED and still exported from icons.jsx.
// OLD: DownloadIcon, FilterIcon, RefreshIcon, SearchIcon
import {
  DownloadIcon, FilterIcon, SearchIcon,
} from '../../components/platform/icons.jsx'
import { PfPager, PfSortHeader } from '../../components/platform/pagination.jsx'
import PfFilterPanel from '../../components/platform/FilterPanel.jsx'
// [2026-09-05] ADDED — the supplied teal refresh glyph, used instead of the drawn RefreshIcon.
// It is 36×36 and rendered at 20px, so it downsamples (crisp) rather than upscales.
import refreshTeal from '../../assets/refresh-teal.png'

// The API caps a page at 200 rows (`limit: int = Query(50, ge=1, le=200)` on both paginated
// endpoints), so an export walks the result set in 200-row requests — the fewest round trips the
// server will allow.
const EXPORT_CHUNK = 200
// And it stops after this many rows. A customer with a year of traffic has hundreds of thousands of
// request rows; walking all of them from a browser tab is a self-inflicted outage. When the cap is
// hit the user is TOLD the file is partial — a truncated export that looks complete is worse than
// no export, which is the same reason the per-page button says "Export page".
const EXPORT_MAX_ROWS = 10000

/** Debounce a fast-changing value. Returns the value only after it has been still for `delay`. */
function useDebounced(value, delay = 400) {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return settled
}

export default function DataTable({
  title,
  subtitle,
  columns,                 // [{ key, label, render?, align?, width? }]
  fetchPage,               // ({ limit, offset, search, ...filters }) -> { rows, total }
  pageSize = 25,
  searchPlaceholder = 'Search…',
  filters = [],            // [{ key, label, options: [{value,label}] }]
  onRowClick,
  rowKey = (row, i) => row.id ?? i,
  emptyTitle = 'Nothing here yet',
  emptyHint = '',
  csvName,                 // when set, an Export CSV button appears
  // [2026-09-02] ADDED — all four are optional and default to the previous behaviour.
  pageSizeOptions = [25, 50, 100],
  initialFilters,          // seed the filters, so another screen can deep-link into a filtered view
  exportAll = true,        // offer "Export all" beside "Export page" when csvName is set
  // [2026-09-05] ADDED — both optional. `filterLabel` names the entity in the panel's headings
  // ("Filter API Requests by", "Select API Requests Date"); `orderByName` offers the third Order By
  // option, which only the sessions endpoint supports.
  filterLabel = 'Activity Logs',
  orderByName = false,
  autoRefreshMs = 0,       // 0 = off. When set, re-fetches the CURRENT page on that interval.
}) {
  const [rawSearch, setRawSearch] = useState('')
  const search = useDebounced(rawSearch)
  // The seed is read ONCE, as initial state. Re-syncing it from the prop on every render would
  // fight the user: clearing the filter would silently re-apply it on the next parent render.
  // A caller that needs a new seed remounts (PlatformApp keys the screen on it).
  const [filterValues, setFilterValues] = useState(() => ({ ...(initialFilters || {}) }))
  const [offset, setOffset] = useState(0)
  const [size, setSize] = useState(pageSize)
  const [state, setState] = useState({ rows: [], total: 0, loading: true, error: null })
  const [nonce, setNonce] = useState(0)      // bumped by Refresh and by the auto-refresh timer
  const [exporting, setExporting] = useState(false)
  // [2026-09-04] ADDED — the Figma's "Filters" control. Seeded OPEN when a filter is already in
  // effect (which is the case when another screen deep-links in via `initialFilters`), so a
  // narrowed table never looks like an empty one with no visible cause.
  const [filtersOpen, setFiltersOpen] = useState(
    () => Object.values(initialFilters || {}).some(Boolean))
  // [2026-09-05] ADDED — { key, dir } | null. Passed THROUGH to fetchPage rather than applied here,
  // so a client-backed table (makeClientPager) sorts its whole set and a server-backed one would
  // sort in the query. Sorting the rows already in state would only ever order the visible page.
  const [sort, setSort] = useState(null)

  // A NEW SEARCH, FILTER OR PAGE SIZE MUST RESET TO PAGE 1. Without this, narrowing a 400-row list
  // to 3 rows while sitting on page 5 shows an empty table and reads as "no results" when there are
  // three. Page size belongs in the same list for the same reason: row 300 is page 12 at 25/page
  // and page 3 at 100/page, so keeping the OFFSET while changing the size lands somewhere arbitrary.
  const filterSignature = JSON.stringify(filterValues)
  // [2026-09-05] `sort` joins this list for the same reason as the rest: re-ordering 191 rows while
  // sitting on page 4 shows a window of a set the user has not seen the top of.
  useEffect(() => { setOffset(0) }, [search, filterSignature, size, sort])

  // Guards against a slow response for an OLD query landing after a fast one for the new query and
  // repainting the table with rows that no longer match what the search box says.
  const requestId = useRef(0)

  // The current query, in one place: the page fetch, the refresh and the export all describe the
  // same result set, and three hand-built copies of this object is how an export quietly stops
  // honouring a filter that the table on screen is applying.
  const query = useMemo(
    // `quick` is panel-only bookkeeping (which radio is lit) and must NOT reach fetchPage — it is
    // a restatement of dateFrom/dateTo, and passing it would send the API a parameter it has no
    // meaning for.
    () => { const { quick, ...applied } = filterValues
            return { search: search || undefined, ...applied,
                     sortKey: sort?.key, sortDir: sort?.dir } },
    [search, filterSignature, sort],   // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    const mine = ++requestId.current
    let live = true
    setState((s) => ({ ...s, loading: true, error: null }))
    fetchPage({ limit: size, offset, ...query })
      .then((data) => {
        if (!live || mine !== requestId.current) return
        setState({ rows: data.rows || [], total: data.total || 0, loading: false, error: null })
      })
      .catch((err) => {
        if (!live || mine !== requestId.current) return
        setState({ rows: [], total: 0, loading: false, error: err })
      })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, size, query, nonce])

  // [2026-09-02] ADDED — optional polling. OFF unless a caller asks for it, and it only ever bumps
  // the nonce, so it reuses the one fetch path above rather than becoming a second one that could
  // drift from it. The stale-response guard already covers a poll landing after a manual reload.
  useEffect(() => {
    if (!autoRefreshMs) return undefined
    const timer = setInterval(() => setNonce((n) => n + 1), autoRefreshMs)
    return () => clearInterval(timer)
  }, [autoRefreshMs])

  const { rows, total, loading, error } = state
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + rows.length, total)
  const page = Math.floor(offset / size) + 1
  const pages = Math.max(1, Math.ceil(total / size))
  // [2026-09-05] CHANGED — canPrev / canNext / lastOffset are gone with the bespoke pager that used
  // them; PfPager derives its own enabled/disabled state and page list from (page, pageSize, total).
  // OLD:
  //   const canPrev = offset > 0
  //   const canNext = offset + size < total
  //   const lastOffset = Math.max(0, (pages - 1) * size)   // floor-to-page, so "Last" never
  //                                                        // showed a window overlapping the one
  //                                                        // before it

  // [2026-09-25] CHANGED — `order` is no longer counted. The new date-column arrows set it, and an
  // arrow click then read "Filters (1)" — as if rows had been hidden, when sorting hides none. The
  // empty-state hint below uses this count too, and "No rows match this filter" is equally wrong
  // for a sort. Every other key is counted exactly as before.
  // OLD: () => Object.values(filterValues).filter(Boolean).length, [filterValues])
  const activeFilterCount = useMemo(
    () => Object.entries(filterValues).filter(([key, value]) => key !== 'order' && Boolean(value)).length,
    [filterValues])

  // [2026-09-25] ADDED — the server `order` as the { key, dir } an arrow header draws. Only an
  // explicit asc/desc lights the arrow; unset (the server default, newest first) and "name" leave it
  // neutral, so the first click goes to OLDEST first — the order the person is not already seeing.
  const orderAsSort = (c) => ((filterValues.order === 'asc' || filterValues.order === 'desc')
    ? { key: c.sortKey || c.key, dir: filterValues.order }
    : null)

  // [2026-09-02] ADDED — export the WHOLE filtered result set, not just the visible page.
  //
  // It walks the same `fetchPage` the table uses, with the same query, SEQUENTIALLY: this is a
  // customer-facing API with rate limits, and firing forty concurrent page requests to save a few
  // seconds is how an export gets a tenant throttled. It stops on the first short page, so it costs
  // no extra round trip at the end, and it never asks for more than EXPORT_MAX_ROWS.
  const exportEverything = useCallback(async () => {
    setExporting(true)
    try {
      const collected = []
      let cursor = 0
      let truncated = false
      for (;;) {
        const data = await fetchPage({ limit: EXPORT_CHUNK, offset: cursor, ...query })
        const batch = data.rows || []
        collected.push(...batch)
        cursor += batch.length
        if (batch.length < EXPORT_CHUNK) break          // short page = end of the result set
        if (cursor >= (data.total || 0)) break
        if (collected.length >= EXPORT_MAX_ROWS) { truncated = true; break }
      }
      if (!collected.length) { toast('Nothing to export.', true); return }
      downloadCsv(`${csvName}-all.csv`, collected)
      if (truncated) {
        toast(`Exported the first ${num(collected.length)} rows — the export is capped.`, true)
      } else {
        toast(`Exported ${num(collected.length)} rows.`)
      }
    } catch (err) {
      toast(err?.message || 'Export failed.', true)
    } finally {
      setExporting(false)
    }
  }, [fetchPage, query, csvName])

  return (
    <div className="pf-card">
      {(title || subtitle) && (
        <div className="pf-card-head">
          <div>
            {title && <h3>{title}</h3>}
            {subtitle && <p>{subtitle}</p>}
          </div>
        </div>
      )}

      {/* ── the toolbar ──────────────────────────────────────────────────── */}
      <div className="pf-toolbar">
        <div className="pf-search">
          {/* aria-label="Search" is REQUIRED — e2e/platform-dashboard.spec.js:94 finds this box
              with getByLabel('Search'). The placeholder is not an accessible name. */}
          <input value={rawSearch} onChange={(e) => setRawSearch(e.target.value)}
                 placeholder={searchPlaceholder} aria-label="Search" />
          <SearchIcon />
        </div>

        <div className="pf-toolbar-spacer" />

        {/* [2026-09-02] A manual reload. These tables are a live feed of an exam in progress;
            without this the only way to see new rows was to change the filter and change it back. */}
        {/* [2026-09-24] CHANGED — this button now RESETS and reloads, and shows the Figma image
            without a second frame around it (asked for by Rujal, 2026-09-24: "it is useful for
            resetting the filter").
            RESET means back to how the table opened: search box cleared, every filter cleared,
            sort back to the default, first page — then a fresh fetch. Previously it only re-ran
            the current query, and the only way to clear filters was the "Clear" link inside the
            filter panel, which left the search box as it was.
            OLD:
              <button className="pf-icon-btn accent" disabled={loading}
                      title="Reload this page of results" aria-label="Refresh results"
                      onClick={() => setNonce((n) => n + 1)}>
                <img src={refreshTeal} alt="" aria-hidden="true" width="20" height="20" />
              </button> */}
        <button className="pf-icon-btn accent pf-refresh-btn" disabled={loading}
                title="Reset filters and refresh" aria-label="Reset filters and refresh"
                onClick={() => {
                  setRawSearch('')
                  setFilterValues({})
                  setSort(null)
                  setOffset(0)
                  setFiltersOpen(false)
                  setNonce((n) => n + 1)
                }}>
          <img src={refreshTeal} alt="" aria-hidden="true" width="36" height="36" />
        </button>

        {/* [2026-09-05] CHANGED — the Filters button now opens the shared panel (date range,
            the caller's own selects, and Order By) instead of revealing a row of bare selects. */}
        <div className="pf-filter-wrap">
          <button className="pf-btn ghost" aria-expanded={filtersOpen}
                  onClick={() => setFiltersOpen((open) => !open)}>
            <FilterIcon />
            Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
          </button>
          {filtersOpen && (
            <PfFilterPanel
              label={filterLabel}
              filters={filters}
              value={filterValues}
              showOrderByName={orderByName}
              onClose={() => setFiltersOpen(false)}
              onApply={setFilterValues}
            />
          )}
        </div>

        {csvName && (
          // Exports THE CURRENT PAGE and says so — an "Export" that silently gives you 25 of 4,000
          // rows is worse than no export, because the file looks complete.
          <button className="pf-btn ghost" disabled={!rows.length}
                  onClick={() => downloadCsv(`${csvName}-page-${page}.csv`, rows)}>
            <DownloadIcon />Export page
          </button>
        )}
        {csvName && exportAll && (
          // [2026-09-02] ADDED — the export people actually wanted. Hidden when there is only one
          // page, where it would be the same file as the button beside it under a different name.
          <button className="pf-btn ghost" disabled={exporting || !total || pages <= 1}
                  onClick={exportEverything}>
            <DownloadIcon />{exporting ? 'Exporting…' : `Export all (${num(total)})`}
          </button>
        )}
      </div>

      {/* [2026-09-05] CHANGED — the revealed row of selects and the separate Clear button are
          gone; both live in the panel now. Clear there resets AND applies in one action.
          OLD: a <div className="pf-toolbar"> of <select>s, plus a second one holding Clear. */}

      {error && <PfNotice kind="error">{error.message}</PfNotice>}

      {loading ? (
        <PfSkeleton height={240} />
      ) : rows.length === 0 ? (
        <PfEmpty title={emptyTitle}
                 hint={(rawSearch || activeFilterCount)
                   ? 'No rows match this search or filter.' : emptyHint} />
      ) : (
        <div className="pf-table-wrap">
          <table className="pf-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={c.align === 'right' ? 'num' : undefined}>
                    {/* onSort is passed ONLY for a column the caller marked sortable — see the
                        note in PfSortHeader about why the two server-paginated tables have none. */}
                    {/* [2026-09-25] CHANGED — a column marked `orderSort` gets the up/down arrow too,
                        driving the SERVER's newest/oldest `order` instead of a local sort.
                        Asked for by Rujal: an arrow on every table's date column.

                        WHY THIS DOES NOT BREAK THE RULE AT THE TOP OF THIS FILE ("NOT ADDED,
                        DELIBERATELY: column sorting"). That rule forbids sorting only the rows
                        already on screen. This does not sort rows at all: it sets the same
                        `order` value the filter panel's "Order By" sets, which every
                        server-backed fetchPage already forwards (GET /me/requests, /me/sessions
                        and /me/video-jobs all take order=asc|desc — app/core/me_analytics.py
                        _dir / _sessions_sort / _jobs_sort). So the whole result set is ordered,
                        across every page. A caller marks ONLY the column the server actually
                        orders by (`ts` or `created_at`), never another date it cannot sort.

                        The arrow and the panel's radio are the same state, so they cannot
                        disagree; Clear and the reset button return both to the default.
                        OLD:
                    <PfSortHeader label={c.label} sortKey={c.sortKey || c.key}
                                  sort={sort} align={c.align}
                                  onSort={c.sortable
                                    ? (key, dir) => setSort({ key, dir })
                                    : undefined} /> */}
                    <PfSortHeader label={c.label} sortKey={c.sortKey || c.key}
                                  sort={c.orderSort ? orderAsSort(c) : sort} align={c.align}
                                  onSort={c.sortable
                                    ? (key, dir) => setSort({ key, dir })
                                    : c.orderSort
                                      ? (key, dir) => setFilterValues((v) => ({ ...v, order: dir }))
                                      : undefined} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                // [2026-09-02] CHANGED — a clickable row is now reachable from the keyboard.
                //
                // OLD:
                //   <TableRow key={rowKey(row, i)}
                //             onClick={onRowClick ? () => onRowClick(row) : undefined}
                //             style={onRowClick ? { cursor: 'pointer' } : undefined}>
                //
                // A bare onClick on a <tr> is mouse-only: a <tr> takes no focus and fires no click
                // from Enter, so opening a candidate's report was impossible without a pointer.
                // This is the ONE screen a reviewer makes a decision about a person on.
                //
                // tabIndex + a keydown handler, and DELIBERATELY NOT role="button". Two reasons,
                // both already paid for once in this repo. (1) An explicit role REPLACES the
                // implicit one — the note in CLAUDE.md about role="tab" on a <button> breaking
                // every automated sign-in is this same mechanism; role="button" on a <tr> takes the
                // row out of the table's row/grid structure, so a screen reader stops announcing
                // "row 4 of 25" and the table stops being navigable as a table. (2) Playwright's
                // getByRole('button', {name}) matches the accessible name as a SUBSTRING by
                // default, and a row's accessible name is its whole concatenated text — every row
                // would become a candidate match for the suites' button locators.
                // Space is preventDefault-ed because its default on a focused element is to scroll.
                <tr key={rowKey(row, i)}
                    className={onRowClick ? 'clickable' : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={onRowClick ? (e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return
                      e.preventDefault()
                      onRowClick(row)
                    } : undefined}>
                  {columns.map((c) => (
                    <td key={c.key} className={c.align === 'right' ? 'num' : undefined}>
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* [2026-09-05] CHANGED — the footer is now the account app's shape: the "Showing X to Y of
          N entries" line, a rows-per-page select, and the NUMBERED pager with ellipses ported from
          frontend-account (see components/platform/pagination.jsx).

          OLD: a bespoke pair of circular chevron buttons with the current page between them, and a
          counter reading "Showing 1–10 of 50 entries · page 1 of 8". It worked, but it was a third
          pagination design in a product that already had one — which is the whole complaint.

          THE COUNTER WORDING IS NOW THEIRS, "to" instead of an en dash. That string is asserted in
          e2e/platform-dashboard.spec.js, and the spec is updated in this same commit rather than
          the wording being bent to keep an old regex passing. */}
      <div className="pf-pager">
        <div className="pf-pager-count">
          {total === 0
            ? 'No entries'
            : `Showing ${from} to ${to} of ${num(total)} entries`}
        </div>

        <div className="pf-pager-right">
          {/* [2026-09-02] Rows per page. 25 is right for a glance and wrong for a sweep through a
              day of sessions; the API allows up to 200 per page, so offering 100 costs nothing but
              one larger response. */}
          {pageSizeOptions.length > 1 && (
            <select className="pf-select" aria-label="Rows per page" value={size}
                    onChange={(e) => setSize(Number(e.target.value))}>
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n} entries per page</option>
              ))}
            </select>
          )}

          <PfPager page={page} pageSize={size} total={total}
                   onPageChange={(next) => setOffset((next - 1) * size)} />
        </div>
      </div>
    </div>
  )
}
